import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalStringSchema,
  requiredNumberSchema,
} from 'utils/zodHelpers'

export interface GeckoLinerRegionJSON {
  type: GeckoFeatureModelType.LinerRegion
  name: string
  feature_geometry: {
    x_start: number
    x_end: number
    y_start: number
    y_end: number
  }
  installation_date: string | undefined
  failure_date: string | undefined
}

export const NewGeckoLinerJSON: GeckoLinerRegionJSON = {
  type: GeckoFeatureModelType.LinerRegion,
  name: 'Liner',
  feature_geometry: { x_start: 0, x_end: 0, y_start: 0, y_end: 0 },
  installation_date: undefined,
  failure_date: undefined,
}

export class GeckoLinerRegion extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.LinerRegion
  readonly geometry: GeckoComponentRect
  //TODO: Add a percent corrosion rate when we work the math out
  readonly installationDate: Date | undefined
  failureDate: Date | undefined

  constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    installationDate: Date | undefined,
    failureDate: Date | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.installationDate = installationDate
    this.failureDate = failureDate
  }

  public get isXWrapped() {
    return this.geometry.x.start.num >= this.geometry.x.end.num
  }

  public get isYWrapped() {
    return this.geometry.y.start.num >= this.geometry.y.end.num
  }

  public static CreateFromJSON(
    componentID: string,
    linerID: string,
    linerJSON: GeckoLinerRegionJSON
  ): GeckoLinerRegion {
    const dimensionMeasurementUnits = GeckoMeasurementUnit.Meters

    const featureGeometryData = linerJSON.feature_geometry as Record<
      string,
      number
    >
    const linerGeometry = GeckoComponentRect.CreateFromXYs(
      componentID as string,
      featureGeometryData.x_start as number,
      featureGeometryData.y_start as number,
      featureGeometryData.x_end as number,
      featureGeometryData.y_end as number,
      dimensionMeasurementUnits,
      dimensionMeasurementUnits
    )

    const installationDate =
      linerJSON.installation_date !== undefined
        ? parseDate(linerJSON.installation_date)
        : undefined

    const failureDate =
      linerJSON.failure_date !== undefined
        ? parseDate(linerJSON.failure_date)
        : undefined

    const linerName = linerJSON.name as string

    return new GeckoLinerRegion(
      linerID,
      linerName,
      linerGeometry,
      installationDate,
      failureDate
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoLinerRegionJSON>
  ): Map<string, GeckoLinerRegion> {
    return Object.keys(recordsJSON).reduce((resultObj, linerID) => {
      resultObj.set(
        linerID,
        GeckoLinerRegion.CreateFromJSON(
          componentID,
          linerID,
          recordsJSON[linerID]
        )
      )
      return resultObj
    }, new Map<string, GeckoLinerRegion>())
  }

  asJSON() {
    return generateLiner(
      this.name,
      this.geometry,
      this.installationDate,
      this.failureDate
    )
  }
}

export function generateLiner(
  name: string,
  geometry: GeckoComponentRect,
  installationDate?: Date,
  failureDate?: Date
): GeckoLinerRegionJSON {
  const geometryInMeters = geometry.convertTo(
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const linerJSON: GeckoLinerRegionJSON = {
    type: GeckoFeatureModelType.LinerRegion,
    name: name,
    installation_date: installationDate
      ? installationDate.toISOString().split('T')[0]
      : undefined,
    failure_date: failureDate
      ? failureDate.toISOString().split('T')[0]
      : undefined,
    feature_geometry: {
      x_start: geometryInMeters.x.start.num,
      y_start: geometryInMeters.y.start.num,
      x_end: geometryInMeters.x.end.num,
      y_end: geometryInMeters.y.end.num,
    },
  }

  return linerJSON
}

export const LinerFormFields = (linerJSON: GeckoLinerRegionJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Liner Name',
      value: linerJSON.name,
      unit_magnitude: null,
      initialValue: 'Repair Liner',
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(linerJSON.installation_date, false),
    {
      dataKey: 'failure_date',
      label: 'Failure Date',
      value: linerJSON.failure_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'x_start',
      label: 'X Start',
      value: linerJSON.feature_geometry.x_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_end',
      label: 'X End',
      value: linerJSON.feature_geometry.x_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: linerJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: linerJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const LinerJSONFromFormFields = (formFields: FormField[]) => {
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const failureDate = getFormFieldByDataKey(formFields, 'failure_date')

  return generateLiner(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoComponentRect.CreateFromXYs(
      'fake_component_id',
      getFormFieldByDataKey(formFields, 'x_start')?.value as number,
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      getFormFieldByDataKey(formFields, 'x_end')?.value as number,
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    ),
    installationDate?.value !== undefined
      ? new Date(installationDate?.value as string)
      : undefined,
    failureDate?.value !== undefined
      ? new Date(failureDate?.value as string)
      : undefined
  )
}

export const LinerRegionFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.LinerRegion,
  title: 'Liners',
  singularName: 'Liner',
  fields: [
    'name',
    'x_start',
    'x_end',
    'y_start',
    'y_end',
    'installation_date',
    'failure_date',
  ],
  sortBy: [{ fieldName: 'y_start', order: 'asc' }],
}
